import { memo, useState } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@library';
import { Checkmark } from '../../../core/components/icons/CheckmarkIcon';
import svg835 from '../../../assets/images/835.svg';
import svg836 from '../../../assets/images/836.svg';
import svg838 from '../../../assets/images/838.svg';
import svg846 from '../../../assets/images/846.svg';
import svg848 from '../../../assets/images/848.svg';
import useSearchParamFilterHelper from '../../hooks/useSearchParamFilterHelper';
import useProductFilterCount from '../../queries/useProductFilterCount';
import HorizontalLayout from '../../../core/components/layout/HorizontalLayout';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import styles from './FilterColor.module.scss';
import FilterWrapper from './FilterWrapper';

interface SvgColor {
    isSvg: true;
    image: string;
    name: string;
}

interface HexColor {
    isSvg: false;
    colorHex: string;
    name: string;
}

type Color = SvgColor | HexColor;

export const colors = new Map<string, Color>([
    ['830', { colorHex: '#4A7DB1', isSvg: false, name: 'blue' }],
    ['831', { colorHex: '#D5B090', isSvg: false, name: 'beige' }],
    ['834', { colorHex: '#8E6551', isSvg: false, name: 'brown' }],
    ['835', { image: `url("${svg835}")`, isSvg: true, name: 'colorful' }],
    ['836', { image: `url("${svg836}")`, isSvg: true, name: 'camo' }],
    ['837', { colorHex: '#FFE089', isSvg: false, name: 'yellow' }],
    ['838', { image: `url("${svg838}")`, isSvg: true, name: 'gold' }],
    ['839', { colorHex: '#BABABA', isSvg: false, name: 'grey' }],
    ['840', { colorHex: '#009F51', isSvg: false, name: 'green' }],
    ['841', { colorHex: '#FFA23B', isSvg: false, name: 'orange' }],
    ['842', { colorHex: '#FF65BB', isSvg: false, name: 'pink' }],
    ['843', { colorHex: '#A04CBF', isSvg: false, name: 'purple' }],
    ['844', { colorHex: '#FF000B', isSvg: false, name: 'red' }],
    ['845', { colorHex: '#000000', isSvg: false, name: 'black' }],
    ['846', { image: `url("${svg846}")`, isSvg: true, name: 'silver' }],
    ['847', { colorHex: '#46C6BE', isSvg: false, name: 'turquoise' }],
    ['848', { image: `url("${svg848}")`, isSvg: true, name: 'transparent' }],
    ['849', { colorHex: '#FFFFFF', isSvg: false, name: 'white' }],
    ['3825', { colorHex: '#8D63C6', isSvg: false, name: 'violet' }],
    ['4425', { colorHex: '#EAB2C8', isSvg: false, name: 'rose' }],
]);

const ColorTile = memo(
    ({
        count,
        currentValues,
        value,
        updateCurrentValues,
    }: {
        count?: number;
        currentValues: string[];
        value: string;
        updateCurrentValues?: (value: string, isRemove?: boolean) => void;
    }) => {
        const isSelected = currentValues.includes(value);
        const colorValue = colors.get(value);

        if (!colorValue) {
            return null;
        }

        return (
            <div
                className={styles.colorTileRoot}
                role="button"
                tabIndex={0}
                onClick={() => updateCurrentValues?.(value, isSelected)}
                onKeyPress={e => {
                    if ([' ', 'Enter'].includes(e.key)) {
                        updateCurrentValues?.(value, isSelected);
                    }
                }}
            >
                <HorizontalLayout align="center" gap="small">
                    <div
                        className={clsx(styles.colorTile, {
                            [styles.borderedTile]: value === '849',
                        })}
                        style={{
                            background: colorValue.isSvg
                                ? colorValue.image
                                : colorValue.colorHex,
                        }}
                    >
                        <div className={styles.checkmark}>
                            {isSelected && <Checkmark color="currentColor" />}
                        </div>
                    </div>
                    <Text secondary regular>
                        <span className={styles.itemName}>
                            <FormattedMessage
                                id={colors.get(value)?.name ?? value}
                            />
                        </span>
                        &nbsp;&nbsp;
                        <span className={styles.count}>{count ?? 0}</span>
                    </Text>
                </HorizontalLayout>
            </div>
        );
    }
);

const FilterColor = ({
    name,
    field,
    defaultExpand,
}: {
    name: string;
    field: string;
    defaultExpand?: boolean;
}) => {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const { selectedValues, updateCurrentValues } =
        useSearchParamFilterHelper()(field);

    const { productFilterValues } = useProductFilterCount({
        filterField: field,
    });

    const getCount = (value: string) =>
        productFilterValues.find(countItem => countItem.value === value)?.total;

    const cleanedValues = productFilterValues
        .map(item => item.value)
        .filter(value => colors.get(value))
        .filter(value => {
            const count = productFilterValues.find(
                item => item.value === value
            )?.total;
            return !!count;
        });

    if (cleanedValues.length === 0) {
        return null;
    }

    const processedValues = cleanedValues.filter(value =>
        intl
            .formatMessage({ id: colors.get(value)?.name ?? value })
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    return (
        <FilterWrapper
            name={name}
            setSearch={setSearch}
            defaultExpand={defaultExpand}
            items={processedValues.map(value => ({
                render: setShowFullList => (
                    <ColorTile
                        count={getCount(value)}
                        key={`filter_color_${value}`}
                        currentValues={selectedValues}
                        value={value}
                        updateCurrentValues={(
                            newValue: string,
                            isRemove?: boolean,
                            isReplace?: boolean
                        ) => {
                            setShowFullList(false);
                            updateCurrentValues(newValue, isRemove, isReplace);
                        }}
                    />
                ),
                isSelected: selectedValues.includes(value),
            }))}
        />
    );
};

export default memo(withErrorBoundary(FilterColor));
